import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaRegBell, FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import "../assets/css/header.css";
import "../styles/navbar.css";
import logo from "../assets/images/navbarlogo.webp";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false); // mobile menu
  const [showSectionsDropdown, setShowSectionsDropdown] = useState(false); // desktop sections dropdown
  const [showUserDropdown, setShowUserDropdown] = useState(false); // desktop user dropdown
  const [showMobileDropdown, setShowMobileDropdown] = useState(false); // mobile dropdown
  const sectionsDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const navigate = useNavigate();

  const { user, logout, isAdmin } = useAuth();

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
      setShowSectionsDropdown(false);
      setShowUserDropdown(false);
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    setShowMobileDropdown(false);
  };

  // Close desktop dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sectionsDropdownRef.current && !sectionsDropdownRef.current.contains(e.target)) {
        setShowSectionsDropdown(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="header">
      <div className="header-container">
        {/* Logo and Brand */}
        <div className="logo-container">
          <img src={logo} alt="Gramture Logo" className="logo" />
          <Link to="/" className="site-name" onClick={handleLinkClick}>
            Gramture
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="nav-links">
          <Link to="/" className="nav-link-item">Home</Link>

          {/* Sections Dropdown */}
          <div
            className="dropdown-wrapper"
            onMouseEnter={() => setShowSectionsDropdown(true)}
            onMouseLeave={() => setShowSectionsDropdown(false)}
            ref={sectionsDropdownRef}
          >
            <span className="dropdown-title">
              Study Material <FaChevronDown size={10} className="dropdown-arrow" />
            </span>
            {showSectionsDropdown && (
              <div className="dropdown-panel">
                <Link to="/sections/Letters" onClick={handleLinkClick}>Letters</Link>
                <Link to="/sections/Applications" onClick={handleLinkClick}>Applications</Link>
                <Link to="/sections/Essays" onClick={handleLinkClick}>Essays</Link>
                <Link to="/sections/Translation" onClick={handleLinkClick}>Translation</Link>
                <Link to="/sections/Applied Grammar" onClick={handleLinkClick}>Applied Grammar</Link>
                <Link to="/sections/Idioms" onClick={handleLinkClick}>Idioms</Link>
                <Link to="/sections/Direct & Indirect" onClick={handleLinkClick}>Direct & Indirect</Link>
              </div>
            )}
          </div>

          <Link to="/discussionfourm" className="nav-link-item">Discussion</Link>
          <Link to="/ourproud" className="nav-link-item">Achievers</Link>
          <Link to="/about" className="nav-link-item">About</Link>
        </nav>

        {/* Right Section Actions */}
        <div className="right-section">
          <Link to="/gramturestore" className="store-link">
            Store
          </Link>

          {user ? (
            <div className="user-dropdown-container" ref={userDropdownRef}>
              <div
                className="user-menu-trigger"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <div className="user-avatar-placeholder">
                  {user.email[0].toUpperCase()}
                </div>
                <span className="user-email-text">{user.email}</span>
              </div>
              {showUserDropdown && (
                <div className="user-dropdown-menu">
                  <div className="user-dropdown-header">{user.email}</div>
                  {isAdmin && (
                    <Link to="/admin-dashboard" className="user-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                      Admin Panel
                    </Link>
                  )}
                  <button className="user-dropdown-item logout" onClick={handleLogout}>
                    Log Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="nav-auth-buttons">
              <Link to="/login" className="sign-in-btn">Sign In</Link>
            </div>
          )}

          {/* Mobile Menu Toggle Button */}
          <button className="menu-btn" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle Menu">
            {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {isOpen && (
        <div className="mobile-menu">
          <Link to="/" onClick={handleLinkClick}>Home</Link>
          
          <div className="mobile-dropdown">
            <span
              className="dropdown-title"
              onClick={() => setShowMobileDropdown(!showMobileDropdown)}
            >
              Study Material <FaChevronDown size={10} className={`dropdown-arrow ${showMobileDropdown ? "open" : ""}`} />
            </span>
            {showMobileDropdown && (
              <div className="mobile-dropdown-content">
                <Link to="/sections/Letters" onClick={handleLinkClick}>Letters</Link>
                <Link to="/sections/Applications" onClick={handleLinkClick}>Applications</Link>
                <Link to="/sections/Essays" onClick={handleLinkClick}>Essays</Link>
                <Link to="/sections/Translation" onClick={handleLinkClick}>Translation</Link>
                <Link to="/sections/Applied Grammar" onClick={handleLinkClick}>Applied Grammar</Link>
                <Link to="/sections/Idioms" onClick={handleLinkClick}>Idioms</Link>
                <Link to="/sections/Direct & Indirect" onClick={handleLinkClick}>Direct & Indirect</Link>
              </div>
            )}
          </div>

          <Link to="/discussionfourm" onClick={handleLinkClick}>Discussion</Link>
          <Link to="/ourproud" onClick={handleLinkClick}>Achievers</Link>
          <Link to="/about" onClick={handleLinkClick}>About</Link>
          <Link to="/gramturestore" onClick={handleLinkClick}>Store</Link>

          {user ? (
            <div className="mobile-auth-actions">
              <span className="user-info-text">
                Logged in: {user.email}
              </span>
              {isAdmin && (
                <Link to="/admin-dashboard" onClick={handleLinkClick} className="admin-link">
                  Admin Panel
                </Link>
              )}
              <button onClick={handleLogout} className="logout-btn">
                Log Out
              </button>
            </div>
          ) : (
            <div className="mobile-auth-actions">
              <Link to="/login" onClick={handleLinkClick} className="sign-in-btn mobile-sign-in">
                Sign In
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
